/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { Form, Formik } from 'formik';
import { Banner, Button, Heading, Stack, Input, Text, TextLink } from '@chaser/components';
import usePush from '../../hooks/usePush';
import AuthenticationService from '../../services/authentication-service';
import LoadingMessage from '../../components/LoadingMessage/index';
import LabeledFieldWithErrorLabel from '../../components/LabeledFieldWithErrorLabel/index.jsx';
import Logo from '../../components/Logo';
import { useDispatch } from 'react-redux';
import { setUserData, setUserLocalStorage } from '../login/duck/actions/login';
import { login } from '../login/duck/actions/login';
import { useCacheActions } from '../../hooks/useFetch';
import OrganisationService from '../../services/organisation-service';
import { cacheLastGoogleSignInUser } from '../login/utils/googleSignInCache';
import style from '../login/AuthPage.module.scss';

const GENERIC_ERROR =
  'Sorry. There’s an issue signing you in with Google right now. Please try again in a moment.';

// eslint-disable-next-line react/prop-types
const GoogleCallback = ({ location: { search } }) => {
  const { execute, reset } = usePush(AuthenticationService.loginGoogleCallback, {
    errorToast: false,
  });
  const history = useHistory();
  const dispatch = useDispatch();
  const { invalidateEndpoint } = useCacheActions();
  const [showLinkPrompt, setShowLinkPrompt] = useState(false);
  const [linkError, setLinkError] = useState(null);

  const handleSuccess = async result => {
    const loginGoogleCallbackResult = result?.data?.loginGoogleCallback ?? {};
    const { user, token: userToken, isExternalAuthentication } = loginGoogleCallbackResult;
    if (!user) {
      history.push('/login', { loginError: GENERIC_ERROR });
      return;
    }

    user.token = userToken;
    user.isExternalAuthentication = isExternalAuthentication;
    cacheLastGoogleSignInUser(user);
    dispatch(setUserData(user));
    history.push('/');
    await invalidateEndpoint(OrganisationService.get);
    setUserLocalStorage(user);
    dispatch(login());
  };

  const apiCall = async password => {
    const [err, result] = await execute(search, password);

    if (err) {
      if (/already exists/i.test(err.message)) {
        setShowLinkPrompt(true);
        return;
      }
      if (/incorrect/i.test(err.message)) {
        setLinkError('Sorry, that password did not work. Please try again.');
        return;
      }
      if (showLinkPrompt) {
        setLinkError(GENERIC_ERROR);
        return;
      }
      history.push('/login', { loginError: GENERIC_ERROR });
      return;
    }

    await handleSuccess(result);
  };

  useEffect(() => {
    apiCall();
  }, []);

  if (showLinkPrompt) {
    return (
      <Stack className={style.authPage} justifyContent="space-between">
        <Stack alignItems="center" justifyContent="center" style={{ flex: 1 }}>
          <div className={style.authTop}>
            <div
              style={{
                width: '430px',
                borderRadius: '8px',
                overflow: 'hidden',
                background: '#fff',
              }}
            >
              <div
                style={{
                  background: '#E8612C',
                  height: '111px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Logo />
              </div>
              <Stack gap="medium" style={{ padding: '30px 40px' }}>
                <Heading as="h2" style={{ textAlign: 'center' }}>
                  Link your account
                </Heading>
                <Text>
                  A Chaser account already exists with this email address. Enter your password to
                  link it with your Google sign-in &mdash; you won&apos;t need to do this again.
                </Text>
                {linkError && (
                  <Banner
                    intent="danger"
                    showCloseButton
                    onClose={() => {
                      setLinkError(null);
                      reset();
                    }}
                  >
                    {linkError}
                  </Banner>
                )}
                <Formik
                  initialValues={{ password: '' }}
                  onSubmit={async (values, actions) => {
                    setLinkError(null);
                    await apiCall(values.password);
                    actions.setSubmitting(false);
                  }}
                >
                  {({ isSubmitting, values }) => (
                    <Form style={{ width: '100%' }}>
                      <Stack gap="medium">
                        <LabeledFieldWithErrorLabel
                          as={Input}
                          name="password"
                          type="password"
                          label="Chaser password"
                          icon="lock"
                          placeholder="Enter your password"
                          width="100%"
                          data-testid="link-password-input"
                          data-trackingid="google-link-password-input"
                          autoComplete="off"
                        />
                        <Button
                          type="submit"
                          loading={isSubmitting}
                          disabled={!values.password}
                          style={{ width: '100%' }}
                          data-testid="link-accounts-button"
                          data-trackingid="google-link-accounts-submit"
                        >
                          Link accounts
                        </Button>
                        <Text textAlign="center" size="small">
                          <TextLink onClick={() => history.push('/login')}>
                            Use a different account
                          </TextLink>
                        </Text>
                      </Stack>
                    </Form>
                  )}
                </Formik>
              </Stack>
            </div>
          </div>
        </Stack>
      </Stack>
    );
  }

  return <LoadingMessage center useLogoAnimation />;
};
export default GoogleCallback;
